import React from 'react';
import {Form, Button, Container, Row, Col, Card} from 'react-bootstrap';
import {useForm} from "@inertiajs/react";
import {useLaravelReactI18n} from "laravel-react-i18n";

/**
 * Lokale Entsprechung von laravel-inertia-backend/components/Pages/Settings.jsx.
 *
 * Einziger inhaltlicher Unterschied: Werte vom Typ "text" (z. B. lange
 * E-Mail-Empfängerlisten) werden über ein <textarea> statt ein <input>
 * gerendert. Ein natives <input> kann seinen Wert unter keinen Umständen
 * per CSS umbrechen - das ist eine feste Browser-Grenze, kein Styling-
 * Problem. Auf breiten Fenstern verhält sich das <textarea> per CSS
 * (siehe app.scss, .settings-value-control) exakt wie das bisherige
 * <input> (einzeilig, Text läuft intern weiter statt umzubrechen); erst
 * unterhalb der Bootstrap-lg-Grenze wird echtes Umbrechen erlaubt.
 */
export default function Settings({settings}) {
    const {t} = useLaravelReactI18n();

    // Verwandle die gruppierten Einstellungen in ein flaches Objekt für das Formular
    const {data, setData, post, errors} = useForm(
        Object.values(settings).flat().reduce((prev, setting) => ({
            ...prev,
            [setting.key]: setting.value,
        }), {})
    );

    const handleSubmit = (e) => {
        e.preventDefault();
        post('settings');
    };

    // Ein <textarea> fügt bei Enter einen Zeilenumbruch ein, ein <input>
    // löst stattdessen das Formular aus - dieser Unterschied würde sonst
    // in gespeicherten Werten (z. B. Empfängerlisten) unbemerkt einen
    // Zeilenumbruch hinterlassen.
    const submitOnEnter = (e) => {
        if (e.key !== 'Enter') {
            return;
        }
        e.preventDefault();
        e.currentTarget.form?.requestSubmit();
    };

    return (
        <Container className="mt-3">
            <h1>{t('system settings')}</h1>
            <Form className={"mb-5"} onSubmit={handleSubmit}>
                {Object.entries(settings).map(([category, categorySettings]) => (
                    <Card key={category} className="mt-3">
                        <Card.Header as={"h5"}>{t(category)}</Card.Header>
                        <Card.Body>
                            {categorySettings.map((setting) => (
                                <Form.Group as={Row} className="mb-3" controlId={setting.key} key={setting.key}>
                                    <Form.Label column sm="3">{t(setting.label)}</Form.Label>
                                    <Col sm="9">
                                        {setting.type === 'select' ? (
                                            <Form.Select
                                                className={errors[setting.key] ? "is-invalid" : ""}
                                                value={data[setting.key]}
                                                onChange={(e) => setData(setting.key, e.target.value)}
                                            >
                                                {setting.options.map((option) => (
                                                    <option key={option.id} value={option.id}>
                                                        {option.label}
                                                    </option>
                                                ))}
                                            </Form.Select>
                                        ) : setting.type === 'integer' || setting.type === 'decimal' ? (
                                            <Form.Control
                                                type="number"
                                                className={errors[setting.key] ? "is-invalid" : ""}
                                                value={data[setting.key]}
                                                onChange={(e) => setData(setting.key, e.target.value)}
                                            />
                                        ) : (
                                            <Form.Control
                                                as="textarea"
                                                rows={1}
                                                className={`settings-value-control${errors[setting.key] ? " is-invalid" : ""}`}
                                                value={data[setting.key]}
                                                onChange={(e) => setData(setting.key, e.target.value)}
                                                onKeyDown={submitOnEnter}
                                            />
                                        )}
                                        {errors[setting.key] && (
                                            <div className="invalid-feedback">{errors[setting.key]}</div>
                                        )}
                                    </Col>
                                </Form.Group>
                            ))}
                        </Card.Body>
                    </Card>
                ))}
                <Button className={"mt-4"} variant="primary" type="submit">Speichern</Button>
            </Form>
        </Container>
    );
}
